import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { intlShape, injectIntl, FormattedMessage } from 'react-intl';
import {
  DataTable,
  DataTableColumn,
  DataTableCell,
  Input,
  InputIcon
} from '@salesforce/design-system-react';
import { Chart } from '@mc-prime/einstein-ui-components';
import { ANALYTICS_COLORS } from '../../constants';

import './AssetSummaryBreakdown.scss';

const CustomDataTableCell = ({ children, property, ...props }) => {
  const { formatNumber } = props.intl;
  switch (property) {
    case 'label':
      return <td className="truncate">{children}</td>;
    case 'percent_total':
      const percentTotal = props.item.active / props.total;
      return (
        <td className="truncate">
          {formatNumber(percentTotal, {
            style: 'percent',
            maximumFractionDigits: 2
          })}
        </td>
      );
    default: {
      return <td>{children}</td>;
    }
  }
};

CustomDataTableCell.displayName = DataTableCell.displayName;
CustomDataTableCell.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.object
  ]),
  property: PropTypes.string.isRequired,
  onclick: PropTypes.func,
  formatMessage: PropTypes.func,
  item: PropTypes.object,
  numberStyle: PropTypes.object
};

const noValue = '#ecebea';
const others = '#e3A85b';

export class AssetSummaryBreakdown extends Component {
  headerCell(property, formatMessage) {
    return formatMessage({
      id: `components.ecs.distribution.${property}`
    });
  }

  columns() {
    return {
      label: { sortable: false },
      active: { sortable: true },
      percent_total: { sortable: false },
      expiring_7_days: { sortable: false },
      expiring_30_days: { sortable: false },
      expired: { sortable: false }
    };
  }

  valueColors(rows) {
    const dots = {};
    rows.forEach(row => {
      const idx = rows.indexOf(row);
      let color;
      switch (row.value) {
        case '_null':
          color = noValue;
          break;
        case '_others':
          color = others;
          break;
        default:
          color = ANALYTICS_COLORS[idx];
          break;
      }
      dots[row.label] = color;
    });
    return dots;
  }
  rowsMetadata(rows) {
    const { formatMessage } = this.props.intl;
    return [
      {
        column: 'label',
        type: 'dimension',
        label: formatMessage({
          id: 'components.ecs.distribution.chart.attributeValue'
        }),
        valueColors: this.valueColors(rows)
      },
      {
        column: 'active',
        type: 'measure',
        label: formatMessage({
          id: 'components.ecs.distribution.chart.availableAssets'
        }),
        format: ['###,##', 1]
      }
    ];
  }

  mapDistribution(func) {
    if (this.props.distribution) {
      let keys = Object.keys(this.props.distribution).filter(
        key => key !== '_null'
      );
      // add the "null" key if it exists
      if (Object.keys(this.props.distribution).includes('_null')) {
        keys.push('_null');
      }

      return keys.map(key => func(key, this.props.distribution[key]));
    }
    return [];
  }

  tableData() {
    return this.mapDistribution((key, val) => {
      const { formatMessage } = this.props.intl;
      let label;
      switch (key) {
        case '_null':
          label = formatMessage({
            id: 'components.ecs.distribution.table.noValues'
          });
          break;
        case '_others':
          label = formatMessage({
            id: 'components.ecs.distribution.table.others'
          });
          break;
        default:
          label = key;
          break;
      }
      return {
        id: key.toString(),
        value: key,
        label,
        active: val.active,
        ...val
      };
    });
  }

  scrollMode() {
    return !(!this.props.hasMore && this.props.distribution?.keys?.length < 10);
  }

  distributionHeader() {
    const filter = this.props.filter || {};
    const { formatMessage } = this.props.intl;

    const count = this.props.summary?.total || 0;

    // item count
    const itemCount = formatMessage(
      { id: 'components.ecs.distribution.breakdown.items' },
      { count }
    );

    // all counters
    const counters = [itemCount];

    // filter count
    if (filter?.query) {
      const filterCount = Object.keys(this.props.distribution).length;
      if (filterCount > 0) {
        counters.push(
          formatMessage(
            { id: 'components.ecs.distribution.header.filter' },
            { count: filterCount }
          )
        );
      }
    }
    counters.push(
      formatMessage({ id: 'components.ecs.distribution.header.sort' })
    );

    // provide the header body and join all counters together separated by bullets
    const body = (
      <div className="distribution-header-title slds-col">
        <h2>
          <FormattedMessage id="components.ecs.distribution.tag.title" />
        </h2>
        {counters.join(' • ')}
      </div>
    );
    return body;
  }

  renderSearch() {
    const { formatMessage } = this.props.intl;
    const searchPlaceholder = formatMessage({
      id: 'components.ecs.distribution.tag.filter.namePlaceholder'
    });

    return (
      <div className="distribution-header-actions slds-col">
        <Input
          className="filter-by-name"
          iconLeft={
            <InputIcon
              assistiveText={{
                icon: 'Search'
              }}
              name="search"
              category="utility"
            />
          }
          value={this.props.filter.query || ''}
          onChange={(evt, { value }) => {
            this.props.filter.query = value;
            this.props.onSearch();
          }}
          placeholder={searchPlaceholder}
          //hasSpinner={this.props.loadContext.browser}
        />
      </div>
    );
  }

  render() {
    const { formatMessage } = this.props.intl;
    const chartRows = this.tableData();
    const tableRows = chartRows.filter(
      row => this.scrollMode() || row.value !== '_null'
    );
    const borderClass = tableRows.length < 6 ? 'border-bottom' : '';
    const chartConfig = {
      inner: 70,
      color: {
        column: ['label']
      },
      dimension: {
        column: ['label']
      },
      selectMode: 'single',
      valueType: 'none',
      centerValueType: 'compactNumber',
      showMeasureTitle: false,
      plots: [
        {
          column: ['active']
        }
      ],
      legend: {
        position: 'right'
      },
      title: {
        label: formatMessage({
          id: 'components.ecs.distribution.total'
        })
      },
      theme: {
        base: 'wave'
      }
    };

    const showChart = !this.props.hasMore && tableRows.length < 10;
    const chart = (
      <div className="asset-breakdown-chart">
        <div className="breakdown-chart-title">
          <FormattedMessage id="components.ecs.distribution.breakdown.title" />
        </div>
        <div className="pie-chart-container slds-col">
          {chartRows.length ? (
            <Chart
              chartModule="sfdc-pie-chart"
              modulePrefix="../"
              rows={chartRows}
              config={chartConfig}
              rowsMetadata={this.rowsMetadata(chartRows)}
              width={375}
              height={300}
            />
          ) : null}
        </div>
      </div>
    );
    return (
      <div className="attribute-values-breakdown">
        <div className="distribution-header slds-grid slds-grid_align-spread">
          {this.distributionHeader()}
          {this.renderSearch()}
        </div>

        <div className="asset-breakdown">
          {showChart ? chart : null}
          <div className="asset-breakdown-table">
            <DataTable
              fixedHeader
              fixedLayout
              items={tableRows}
              className={borderClass}
              hasMore={this.props.hasMore}
              onLoadMore={this.props.onLoadMore}
              onSort={sort => {
                this.props.filter.sortColumn = {};
                this.props.filter.sortColumn.property = sort.property;
                this.props.filter.sortColumn.sortDirection = sort.sortDirection;
                this.props.onSearch();
              }}
            >
              {Object.entries(this.columns()).map((column, index) => {
                const sortable = column[1].sortable;
                const width = column[1].width;
                const property = column[0];
                return (
                  <DataTableColumn key={index} sortable={sortable}>
                    <CustomDataTableCell
                      property={property}
                      formatMessage={formatMessage}
                      label={this.headerCell(property, formatMessage)}
                      width={width}
                      total={this.props.summary?.total}
                      intl={this.props.intl}
                    />
                  </DataTableColumn>
                );
              })}
            </DataTable>
          </div>
        </div>
      </div>
    );
  }
}

AssetSummaryBreakdown.propTypes = {
  actions: PropTypes.object,
  summary: PropTypes.object,
  distribution: PropTypes.object,
  filter: PropTypes.object,
  hasMore: PropTypes.bool,
  onLoadMore: PropTypes.func,
  onSearch: PropTypes.func,
  onSort: PropTypes.func,
  location: PropTypes.object,
  intl: intlShape.isRequired
};

export default injectIntl(AssetSummaryBreakdown);
